import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, BarChart3, Calendar, ChevronDown, Eye, Lightbulb, MousePointerClick, TrendingUp } from 'lucide-react';

import { ShopMap } from '@/components/ads/ShopMap';
import { AdsPerformanceChart } from '@/components/ads/AdsPerformanceChart';
import { ChannelSectionHeader } from '@/components/results/ChannelSectionHeader';
import { ChannelSyncOnlyResults } from '@/components/results/ChannelSyncOnlyResults';
import { InteractionsBreakdownChart, InteractionsTimeChart, MapsSearchChart, ProfileViewsChart } from '@/components/results/GbpCharts';
import { ColumnTable } from '@/components/ui/column-table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { KpiTile } from '@/components/ui/kpi-tile';
import { SectionHeader } from '@/components/ui/section-header';
import { Skeleton } from '@/components/ui/skeleton';
import { SoftCard } from '@/components/ui/soft-card';
import { useChannels } from '@/hooks/useChannels';
import { useChannelDefinitions } from '@/hooks/useChannelDefinitions';
import { useDateRange, DATE_RANGE_OPTIONS } from '@/hooks/useDateRange';
import { useGbpInsights, useLocalAds, useStockTitles, useStorefrontInsights, useStorefrontPopularProducts } from '@/hooks/useInsights';
import { useOrders } from '@/hooks/useOrders';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { buildChannel } from '@/lib/channels';
import { LIA_INSIGHT_METRICS, SUMMARY_ROWS } from '@/lib/liaInsights';
import { cn } from '@/lib/utils';
import { PageScore } from '@/components/overview/LocalPotentialHeader';

const numberFormatter = new Intl.NumberFormat('en-GB');
const currencyFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
const formatNumber = (n) => numberFormatter.format(Number(n) || 0);

const CHANNEL_FILTERS = [
	{ value: 'all', label: 'All', channelIds: [] },
	{ value: 'google', label: 'Google Local Listings', channelIds: ['google'] },
	{ value: 'local-ads', label: 'Google Local Inventory Ads', channelIds: ['local-ads'] },
	{ value: 'meta-commerce', label: 'Meta Commerce', channelIds: ['meta-commerce'] },
	{ value: 'storefront', label: 'Local Storefront', channelIds: ['storefront'] },
	{ value: 'product-reservations', label: 'Reservations', channelIds: ['product-reservations'] },
	{ value: 'local-checkout', label: 'Local Checkout', channelIds: ['local-checkout'] }
];

const PILL_BUTTON_CLASS =
	'inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-semibold text-foreground transition hover:bg-muted/50';

/**
 * Date range card — soft-gradient rounded surface wrapping the three
 * controls. Lives inside the "Results by channel" section above the
 * filter pills, mirroring the Lovable design.
 */
function DateRangeCard({ value, label, onChange }) {
	return (
		<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border/60 bg-gradient-soft px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:px-5 md:py-4">
			<span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Showing data for</span>
			<div className="flex flex-wrap items-center gap-2">
				<DropdownMenu>
					<DropdownMenuTrigger className={PILL_BUTTON_CLASS}>
						<Calendar className="h-4 w-4" /> {label}
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						{DATE_RANGE_OPTIONS.map((o) => (
							<DropdownMenuItem
								key={o.value}
								onSelect={() => onChange(o.value)}
								className={o.value === value ? 'font-semibold' : ''}
							>
								{o.label}
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
				<span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">vs</span>
				{/*
				 * The comparison and granularity controls only have one option each
				 * for now (we always compare to the immediate previous period and
				 * always aggregate daily). The dropdowns are kept so the page
				 * visually matches the Lovable design — and so adding "Same period
				 * last year" / "Weekly" / "Monthly" later doesn't change the layout.
				 */}
				<DropdownMenu>
					<DropdownMenuTrigger className={PILL_BUTTON_CLASS}>
						Previous period <ChevronDown className="h-4 w-4 text-muted-foreground" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						<DropdownMenuItem className="font-semibold">Previous period</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<DropdownMenu>
					<DropdownMenuTrigger className={PILL_BUTTON_CLASS}>
						Daily <ChevronDown className="h-4 w-4 text-muted-foreground" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						<DropdownMenuItem className="font-semibold">Daily</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	);
}

function ChannelFilterBar({ active, onSelect, available, range }) {
	const filters = CHANNEL_FILTERS.filter((f) => f.value === 'all' || f.channelIds.every((id) => available.includes(id)));
	return (
		<section id="results-by-channel" className="scroll-mt-6 space-y-5">
			<SectionHeader
				eyebrow="Filter"
				title="Results by channel"
				sub="Scroll down to see all results combined, or pick a channel to focus on."
			/>
			<DateRangeCard value={range.value} label={range.label} onChange={range.setRange} />
			<div className="flex flex-wrap gap-2">
				{filters.map((f) => (
					<button
						key={f.value}
						type="button"
						onClick={() => onSelect(f.value)}
						className={cn(
							'rounded-full px-4 py-2 text-sm font-semibold transition',
							active === f.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/70'
						)}
					>
						{f.label}
					</button>
				))}
			</div>
		</section>
	);
}

/**
 * Numbered "1, 2, 3..." list with a label + value per row — used inside
 * SoftCard for the Storefront's Most viewed products + Traffic sources
 * blocks. Mirrors the rendering shape from the Lovable design's
 * `leftItems` / `rightItems` lists.
 */
function RankedList({ items }) {
	return (
		<ul className="space-y-3">
			{items.map((item, i) => (
				<li key={item.label} className="flex items-center gap-3">
					<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-muted text-xs font-bold text-muted-foreground">
						{i + 1}
					</span>
					<span className="flex-1 truncate text-sm font-medium text-foreground">{item.label}</span>
					<span className="text-sm tabular-nums text-muted-foreground">{item.value}</span>
				</li>
			))}
		</ul>
	);
}

function ChannelLoadingSkeleton() {
	return (
		<section className="space-y-4">
			<Skeleton className="h-14 w-72" />
			<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
				{[0, 1, 2, 3].map((i) => (
					<Skeleton key={i} className="h-[170px]" />
				))}
			</div>
		</section>
	);
}

function GoogleListingsSection({ channel, range }) {
	const { shop } = useShop();
	const gbp = useGbpInsights(shop?.id, range);

	if (gbp.isLoading) return <ChannelLoadingSkeleton />;

	return (
		<section className="space-y-4">
			<ChannelSectionHeader channel={channel} sub={`How this channel is performing over the last ${range.days} days.`} />

			<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
				<KpiTile label="Total profile views" value={formatNumber(gbp.totals.profileViews)} delta={gbp.change.profileViews} />
				<KpiTile label="Search views" value={formatNumber(gbp.totals.searchViews)} delta={gbp.change.searchViews} />
				<KpiTile label="Maps views" value={formatNumber(gbp.totals.mapsViews)} delta={gbp.change.mapsViews} />
				<KpiTile label="Profile interactions" value={formatNumber(gbp.totals.interactions)} delta={gbp.change.interactions} />
			</div>

			<div className="grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-2">
				<SoftCard>
					<SectionHeader title="Profile views over time" sub={`Last ${range.days} days vs previous period.`} />
					{gbp.hasData ? (
						<ProfileViewsChart series={gbp.series} previousSeries={gbp.previousSeries} />
					) : (
						<p className="text-sm text-muted-foreground">
							Google Business Profile views haven't started landing for this shop yet.
						</p>
					)}
				</SoftCard>
				<SoftCard>
					<SectionHeader title="Maps and Search views" sub="Where your profile shows up most." />
					{gbp.hasData ? (
						<MapsSearchChart series={gbp.series} />
					) : (
						<p className="text-sm text-muted-foreground">Nothing to split yet — your first views will appear here.</p>
					)}
				</SoftCard>
			</div>

			<div className="grid grid-cols-1 gap-4 md:gap-5 lg:grid-cols-2">
				<SoftCard>
					<SectionHeader title="Share of profile interactions" sub="How shoppers act after finding you." />
					<InteractionsBreakdownChart website={gbp.totals.website} directions={gbp.totals.directions} phone={gbp.totals.phone} />
				</SoftCard>
				<SoftCard>
					<SectionHeader title="Directions, website and phone clicks" sub={`Last ${range.days} days of profile interactions.`} />
					{gbp.hasData ? (
						<InteractionsTimeChart series={gbp.series} />
					) : (
						<p className="text-sm text-muted-foreground">No interactions yet for this period.</p>
					)}
				</SoftCard>
			</div>
		</section>
	);
}

function LocalAdsSection({ channel, range }) {
	const { shop } = useShop();
	const { data, isLoading } = useLocalAds(shop?.id);
	const { shopPath } = useShopPath();

	if (isLoading) return <ChannelLoadingSkeleton />;

	if (!data?.enabled) {
		return (
			<section className="space-y-4">
				<ChannelSectionHeader channel={channel} sub="Activate Local Ads to start showing in-store stock to nearby shoppers." />
				<SoftCard tone="muted" className="flex flex-wrap items-center gap-4">
					<p className="min-w-0 flex-1 text-sm text-muted-foreground">
						Local Ads isn't switched on yet. Most retailers see between £5 and £15 of in-store sales for every £1 spent.
					</p>
					<Link
						to={shopPath('/channels/local-ads')}
						className="inline-flex items-center gap-1.5 rounded-full bg-action px-5 py-2.5 text-sm font-semibold text-action-foreground transition hover:opacity-90"
					>
						Activate Local Ads <ArrowUpRight className="h-4 w-4" />
					</Link>
				</SoftCard>
			</section>
		);
	}

	const summary = data.summary || {};
	const budget = data.budget || { daily: 0, monthly: 0 };
	const series = Array.isArray(data.daily) ? data.daily : [];
	const topProducts = Array.isArray(data.topProducts) ? data.topProducts : [];
	const searchTerms = Array.isArray(data.searchTerms) ? data.searchTerms : [];
	const locations = Array.isArray(data.locations) ? data.locations : [];
	const clicksToSales = Array.isArray(data.clicksToSales) ? data.clicksToSales : [];

	const monthlySpendDisplay = `${currencyFormatter.format(summary.monthlySpend || budget.monthly || 0)} ad spend this month`;
	const cpcDisplay = summary.cpc ? `£${Number(summary.cpc).toFixed(2)}` : '£0.00';
	const ctrDisplay = `${Number(summary.ctr || 0).toFixed(2)}%`;

	return (
		<section className="space-y-4">
			<ChannelSectionHeader channel={channel} sub={`How your ads are performing over the last ${range.days} days.`} />

			<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
				<KpiTile label="Local shoppers reached" value={formatNumber(summary.impressions)} delta={summary.impressionsChange} />
				<KpiTile label="Clicks to your store" value={formatNumber(summary.clicks)} delta={summary.clicksChange} />
				<KpiTile label="Click-through rate" value={ctrDisplay} sub="Industry average 1.8%" />
				<KpiTile label="Cost per click" value={cpcDisplay} sub={monthlySpendDisplay} />
			</div>

			<SoftCard className="p-5 md:p-6">
				<AdsPerformanceChart series={series} />
			</SoftCard>

			<div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
				<div>
					<SectionHeader title={LIA_INSIGHT_METRICS.products.title} sub={LIA_INSIGHT_METRICS.products.sub} />
					<ColumnTable
						rows={topProducts.slice(0, SUMMARY_ROWS)}
						emptyLabel={LIA_INSIGHT_METRICS.products.emptyLabel}
						viewMoreHref={shopPath('/ads/insights/products')}
						columns={LIA_INSIGHT_METRICS.products.columns}
					/>
				</div>
				<div>
					<SectionHeader title={LIA_INSIGHT_METRICS['search-terms'].title} sub={LIA_INSIGHT_METRICS['search-terms'].sub} />
					<ColumnTable
						rows={searchTerms.slice(0, SUMMARY_ROWS)}
						emptyLabel={LIA_INSIGHT_METRICS['search-terms'].emptyLabel}
						viewMoreHref={shopPath('/ads/insights/search-terms')}
						columns={LIA_INSIGHT_METRICS['search-terms'].columns}
					/>
				</div>
			</div>

			<section>
				<SectionHeader
					eyebrow="Advanced insights"
					title="Where your ads are appearing"
					sub="The local areas where shoppers are seeing and clicking your ads."
				/>
				<div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
					<SoftCard className="overflow-hidden p-0 xl:col-span-2">
						<div className="grid grid-cols-1 md:grid-cols-2">
							<div className="h-full border-b border-border/60 md:border-b-0 md:border-r">
								<ShopMap shop={shop} label={shop?.city || locations[0]?.location} radiusKm={data.radiusKm} />
							</div>
							<div>
								<div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-6 border-b border-border/60 px-5 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
									<div>Location</div>
									<div className="w-16 text-right">Clicks</div>
									<div className="w-20 text-right">Views</div>
								</div>
								<div className="divide-y divide-border/60">
									{locations.length === 0 ? (
										<div className="px-5 py-6 text-center text-sm text-muted-foreground">No geographic data yet.</div>
									) : (
										locations.map((l) => (
											<div
												key={l.location}
												className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-6 px-5 py-3"
											>
												<div className="truncate text-sm font-medium">{l.location}</div>
												<div className="w-16 text-right text-sm font-semibold tabular-nums text-foreground">
													{formatNumber(l.clicks)}
												</div>
												<div className="w-20 text-right text-sm tabular-nums text-muted-foreground">
													{formatNumber(l.views)}
												</div>
											</div>
										))
									)}
								</div>
							</div>
						</div>
					</SoftCard>
					<SoftCard tone="muted">
						<div className="flex items-center gap-2 text-sm font-bold text-foreground">
							<Lightbulb className="h-4 w-4 text-primary" /> How can I use these insights?
						</div>
						<p className="mt-3 text-sm leading-relaxed text-muted-foreground">
							Spotting which areas drive the most clicks helps you decide where to focus stock and promotions. If a nearby
							town is sending lots of views but few clicks, your product titles or photos may need a refresh.
						</p>
					</SoftCard>
				</div>
			</section>

			{clicksToSales.length > 0 ? (
				<section>
					<SectionHeader title={LIA_INSIGHT_METRICS['clicks-to-sales'].title} sub={LIA_INSIGHT_METRICS['clicks-to-sales'].sub} />
					<ColumnTable
						rows={clicksToSales.slice(0, SUMMARY_ROWS)}
						emptyLabel={LIA_INSIGHT_METRICS['clicks-to-sales'].emptyLabel}
						viewMoreHref={shopPath('/ads/insights/clicks-to-sales')}
						columns={LIA_INSIGHT_METRICS['clicks-to-sales'].columns}
					/>
				</section>
			) : null}

			<SoftCard tone="soft" className="flex flex-wrap items-center gap-4">
				<div className="min-w-0 flex-1">
					<h3 className="text-lg font-bold">Your Local Ads are paying back</h3>
					<p className="mt-1 max-w-xl text-sm text-muted-foreground">
						For every £1 spent this month, stores are seeing between £5 and £15 of in-store sales.
					</p>
				</div>
				<Link
					to={shopPath('/ads')}
					className="inline-flex items-center gap-1.5 rounded-full bg-action px-5 py-2.5 text-sm font-semibold text-action-foreground transition hover:opacity-90"
				>
					Adjust budget <ArrowUpRight className="h-4 w-4" />
				</Link>
			</SoftCard>
		</section>
	);
}

function StorefrontSection({ channel, range }) {
	const { shop } = useShop();
	const storefront = useStorefrontInsights(shop?.id, range);
	const { data: popular } = useStorefrontPopularProducts(shop?.id, range);
	const topBarcodes = popular.slice(0, 6).map((p) => p.barcode);
	const { titles } = useStockTitles(shop?.id, topBarcodes);
	const { shopPath } = useShopPath();

	if (storefront.isLoading) return <ChannelLoadingSkeleton />;

	return (
		<section className="space-y-4">
			<ChannelSectionHeader
				channel={channel}
				sub={`How your Searchable Local Storefront is performing over the last ${range.days} days.`}
			/>

			<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
				<KpiTile label="Unique visitors" value={formatNumber(storefront.visitors)} delta={storefront.change.visitors} />
				<KpiTile label="Product page views" value={formatNumber(storefront.productViews)} delta={storefront.change.productViews} />
				<KpiTile
					label="Reservation starts"
					value={formatNumber(storefront.reservationStarts)}
					delta={storefront.change.reservationStarts}
				/>
				<KpiTile
					label="Shop info clicks"
					value={formatNumber(storefront.shopPageClicks)}
					delta={storefront.change.shopPageClicks}
				/>
			</div>

			{popular.length > 0 ? (
				<SoftCard>
					<SectionHeader title="Most viewed products" sub="What shoppers browsed on your storefront." />
					<RankedList
						items={popular.slice(0, 6).map((p) => ({
							label: p.title || titles[p.barcode] || p.barcode,
							value: `${formatNumber(p.views)} views`
						}))}
					/>
				</SoftCard>
			) : !storefront.hasData ? (
				<SoftCard tone="muted">
					<p className="text-sm leading-relaxed text-muted-foreground">
						Your storefront hasn't recorded any visits yet for this period. Share your storefront link in your social profiles
						to drive your first traffic.
					</p>
				</SoftCard>
			) : null}
		</section>
	);
}

const orderTotal = (order) => {
	if (Number.isFinite(Number(order.total))) return Number(order.total);
	if (!Array.isArray(order.products)) return 0;
	return order.products.reduce((sum, p) => sum + Number(p.price || 0) * Number(p.quantity || 1), 0);
};

const summariseOrders = (orders, orderType) => {
	const matching = orders.filter((o) => o.orderType === orderType);
	const completed = matching.filter((o) => o.status === 'completed');
	const totalValue = matching.reduce((sum, o) => sum + orderTotal(o), 0);
	const conversion = matching.length ? Math.round((completed.length / matching.length) * 100) : 0;
	return { placed: matching.length, completed: completed.length, totalValue, conversion };
};

const pctChangeForKpi = (current, previous) => {
	if (!previous) return current ? 100 : null;
	return Number((((current - previous) / previous) * 100).toFixed(1));
};

/**
 * Generic per-channel orders section — feeds both Product Reservations
 * (orderType=reservation) and Local Checkout (orderType=click-collect).
 * Both surfaces share the same shape (count placed, count fulfilled,
 * conversion %, total value) so the components don't duplicate.
 *
 * Calls `useOrders` twice (current + previous window) so the KPI tiles
 * can show period-over-period delta pills like every other channel block
 * on the page rather than static "out of placed" sub-copy.
 */
function OrdersSection({ channel, range, orderType, eyebrow, emptyBody, placedLabel = 'Orders placed' }) {
	const { shop } = useShop();
	const { orders, isLoading } = useOrders(shop?.id, { from: range.from, to: range.to });
	const { orders: previousOrders, isLoading: previousLoading } = useOrders(shop?.id, {
		from: range.lookbackFrom,
		to: range.lookbackTo
	});
	const { shopPath } = useShopPath();

	if (isLoading || previousLoading) return <ChannelLoadingSkeleton />;

	const current = summariseOrders(orders, orderType);
	const previous = summariseOrders(previousOrders, orderType);

	return (
		<section className="space-y-4">
			<ChannelSectionHeader channel={channel} sub={`${eyebrow} over the last ${range.days} days.`} />

			<div className="grid grid-cols-2 gap-4 md:grid-cols-4">
				<KpiTile
					label={placedLabel}
					value={formatNumber(current.placed)}
					delta={pctChangeForKpi(current.placed, previous.placed)}
				/>
				<KpiTile
					label="Completed"
					value={formatNumber(current.completed)}
					delta={pctChangeForKpi(current.completed, previous.completed)}
				/>
				<KpiTile
					label="Conversion"
					value={current.placed ? `${current.conversion}%` : '—'}
					delta={pctChangeForKpi(current.conversion, previous.conversion)}
				/>
				<KpiTile
					label="Total value"
					value={currencyFormatter.format(current.totalValue)}
					delta={pctChangeForKpi(current.totalValue, previous.totalValue)}
				/>
			</div>

			{current.placed === 0 ? (
				<SoftCard tone="muted">
					<p className="text-sm leading-relaxed text-muted-foreground">{emptyBody}</p>
					<p className="mt-3 text-sm">
						<Link to={shopPath('/orders')} className="font-semibold text-primary hover:underline">
							Open Orders →
						</Link>
					</p>
				</SoftCard>
			) : null}
		</section>
	);
}

/**
 * Results page — performance across every live channel.
 *
 * Layout mirrors the Lovable design: a date range pill, a channel-filter
 * strip, then one block per channel built from KPI tiles, charts and
 * tables. Every value is sourced from real insights / orders endpoints —
 * the page never fabricates numbers. Channels that aren't live for the
 * shop are skipped entirely (the filter strip only lists what's live).
 */
export function Results() {
	const { shop, isLoading: shopLoading } = useShop();
	const range = useDateRange();
	const [searchParams, setSearchParams] = useSearchParams();
	const { channels, isLoading: channelsLoading } = useChannels(shop?.id);
	const { definitions, isLoading: defsLoading } = useChannelDefinitions();

	const liveChannelIds = useMemo(() => {
		if (!Array.isArray(channels)) return [];
		return channels.filter((c) => c.status === 'enabled' || c.enabled === true).map((c) => c.type);
	}, [channels]);

	const activeFilter = searchParams.get('channel') ?? 'all';
	const setFilter = (value) => {
		const params = new URLSearchParams(searchParams);
		if (value === 'all') params.delete('channel');
		else params.set('channel', value);
		setSearchParams(params, { replace: true });
	};
	const isVisible = (channelId) => {
		if (!liveChannelIds.includes(channelId)) return false;
		if (activeFilter === 'all') return true;
		const match = CHANNEL_FILTERS.find((f) => f.value === activeFilter);
		return match?.channelIds.includes(channelId) ?? false;
	};

	// Lifted page-level data probes so we can detect the "channels live but
	// no data yet" case before rendering a layout of zeros. Hook keys match
	// what each section calls, so SWR dedupes — no extra network. Each
	// probe is gated on the channel being live for this shop.
	const gbpProbe = useGbpInsights(liveChannelIds.includes('google') ? shop?.id : null, range);
	const adsProbe = useLocalAds(liveChannelIds.includes('local-ads') ? shop?.id : null);
	const storefrontProbe = useStorefrontInsights(liveChannelIds.includes('storefront') ? shop?.id : null, range);
	const ordersProbe = useOrders(
		liveChannelIds.includes('product-reservations') || liveChannelIds.includes('local-checkout') ? shop?.id : null,
		{ from: range.from, to: range.to }
	);
	const probesLoading = gbpProbe.isLoading || adsProbe.isLoading || storefrontProbe.isLoading || ordersProbe.isLoading;
	const hasAnyData =
		(gbpProbe.totals?.profileViews ?? 0) > 0 ||
		(gbpProbe.totals?.interactions ?? 0) > 0 ||
		(adsProbe.data?.summary?.impressions ?? 0) > 0 ||
		(adsProbe.data?.summary?.clicks ?? 0) > 0 ||
		(storefrontProbe.interactions ?? 0) > 0 ||
		(storefrontProbe.visitors ?? 0) > 0 ||
		(ordersProbe.orders?.length ?? 0) > 0;

	if (shopLoading || channelsLoading || defsLoading) {
		return (
			<div className="space-y-10">
				<Skeleton className="h-10 w-48" />
				<Skeleton className="h-8 w-72" />
				<ChannelLoadingSkeleton />
			</div>
		);
	}

	if (liveChannelIds.length === 0) {
		return (
			<div className="space-y-10">
				<header className="flex flex-wrap items-start justify-between gap-4">
					<div>
						<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Results</h1>
						<p className="mt-2 text-muted-foreground">
							What's live, what's working, and what's bringing people into your store.
						</p>
					</div>
					<div className="ml-auto shrink-0">
						<PageScore />
					</div>
				</header>
				<EmptyState
					icon={BarChart3}
					eyebrow="Numbers that mean something"
					title="Your first results are days away"
					body="As soon as your channels go live, we'll show you what's actually moving the needle — local shoppers reached, store visits driven, and revenue earned back."
					primaryCta="Activate your channels"
					primaryHref="/channels"
					previewTitle="Metrics waiting for you"
					preview={[
						{ icon: Eye, label: 'Views', detail: 'On Google Search, Maps & more' },
						{ icon: MousePointerClick, label: 'Engagements', detail: 'Directions, calls and product views' },
						{ icon: TrendingUp, label: 'Reservations & in-store sales', detail: 'See your return on every channel' }
					]}
				/>
			</div>
		);
	}

	// Channels are switched on but nothing has come back from insights /
	// orders yet — that's the "first 24 hours after going live" case.
	// Same warming-up framing the Ads page uses; avoids a full layout
	// of zeros and gives the retailer something to read.
	if (!probesLoading && !hasAnyData) {
		return (
			<div className="space-y-10">
				<header className="flex flex-wrap items-start justify-between gap-4">
					<div>
						<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Results</h1>
						<p className="mt-2 max-w-2xl text-muted-foreground">
							Your channels are live. Most insights start landing within the next 24 hours — your real numbers will appear
							here as soon as the first events come in.
						</p>
					</div>
					<span className="inline-flex items-center gap-1.5 rounded-full bg-warning-soft px-3 py-1.5 text-xs font-semibold text-warning-soft-foreground">
						<span className="h-1.5 w-1.5 rounded-full bg-warning-soft-foreground" /> Warming up
					</span>
				</header>
				<EmptyState
					icon={BarChart3}
					eyebrow="Coming in the next 24 hours"
					title="Your first results are warming up"
					body="It usually takes a few hours for the first events to land. Your impressions, clicks, page views and reservations will appear here once shoppers start finding you."
					previewTitle="What you'll see here"
					preview={[
						{ icon: Eye, label: 'Views', detail: 'On Google Search, Maps & more' },
						{ icon: MousePointerClick, label: 'Engagements', detail: 'Directions, calls and product views' },
						{ icon: TrendingUp, label: 'Reservations & in-store sales', detail: 'See your return on every channel' }
					]}
				/>
			</div>
		);
	}

	const channelsById = (id) => buildChannel(id, definitions, channels);

	return (
		<div className="space-y-10">
			<header className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-3xl font-extrabold tracking-tight md:text-[32px]">Results</h1>
					<p className="mt-2 text-muted-foreground">What's live, what's working, and what's bringing people into your store.</p>
				</div>
				<div className="ml-auto shrink-0">
					<PageScore />
				</div>
			</header>

			<ChannelFilterBar active={activeFilter} onSelect={setFilter} available={liveChannelIds} range={range} />

			{isVisible('google') ? <GoogleListingsSection channel={channelsById('google')} range={range} /> : null}
			{isVisible('local-ads') ? <LocalAdsSection channel={channelsById('local-ads')} range={range} /> : null}
			{isVisible('meta-commerce') ? (
				<ChannelSyncOnlyResults
					channel={channelsById('meta-commerce')}
					platformLabel="Meta Commerce Manager"
					platformUrl="https://business.facebook.com/commerce"
				/>
			) : null}
			{isVisible('storefront') ? <StorefrontSection channel={channelsById('storefront')} range={range} /> : null}
			{isVisible('product-reservations') ? (
				<OrdersSection
					channel={channelsById('product-reservations')}
					range={range}
					orderType="reservation"
					placedLabel="Reservations placed"
					eyebrow="Click-and-collect reservations"
					emptyBody="No reservations placed in this period yet. When shoppers reserve an item online, it'll appear here and in your Orders inbox."
				/>
			) : null}
			{isVisible('local-checkout') ? (
				<OrdersSection
					channel={channelsById('local-checkout')}
					range={range}
					orderType="click-collect"
					placedLabel="Orders placed"
					eyebrow="Local Checkout orders paid via Stripe Connect"
					emptyBody="No checkout orders placed in this period yet. When a shopper buys via Local Checkout, it'll appear here."
				/>
			) : null}
		</div>
	);
}
